/**
 * StudioLogin — frictionless mobile-first OTP onboarding.
 *
 * Two-step flow: enter phone → enter 6-digit OTP. On verify, the backend
 * auto-provisions the workspace and returns a JWT identical in shape to the
 * standard /auth/login response. We persist it via the existing AuthContext
 * mechanism (localStorage `token`) so the rest of the app reuses the same
 * auth pipeline.
 */
import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ChefHat, ArrowRight, RotateCcw, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StudioLogin() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('phone');   // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [betaOtp, setBetaOtp] = useState('');
  const [betaMode, setBetaMode] = useState(false);
  const [devOtp, setDevOtp] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const requestOtp = async () => {
    setError(''); setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/studio/auth/request-otp`, { phone });
      setPhase('otp');
      if (data.beta_mode && data.beta_otp) {
        setBetaMode(true);
        setBetaOtp(data.beta_otp);
        setOtp(data.beta_otp);  // pre-fill so onboarding feels truly frictionless
      }
      if (data.dev_otp) setDevOtp(data.dev_otp);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not send OTP');
    } finally { setSubmitting(false); }
  };

  const verify = async () => {
    setError(''); setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/studio/auth/verify-otp`, {
        phone, otp, name: name.trim() || undefined,
      });
      localStorage.setItem('token', data.token);
      // Soft-reload so AuthContext picks up the new token via /auth/me.
      window.location.href = '/studio/home';
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Verification failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
      data-testid="studio-login-page"
      style={{
        background:
          'radial-gradient(circle at 10% 10%, #FFD7B5 0%, transparent 40%), ' +
          'radial-gradient(circle at 90% 80%, #F4B860 0%, transparent 35%), ' +
          'linear-gradient(180deg, #FFFBF5 0%, #FAF3EA 100%)',
      }}>
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(#3A1F12 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }} />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E8643E] to-[#F4B860] shadow-xl mb-3">
            <ChefHat className="w-7 h-7 text-white" strokeWidth={2.25} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3A1F12] tracking-tight">
            Welcome to Chef Studio
          </h1>
          <p className="text-sm text-[#7A2A12] mt-1.5 max-w-sm mx-auto">
            Build smarter recipes. Cost better. Operate profitably.
          </p>
          <div className="flex items-center justify-center gap-3 text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold mt-3">
            <span>Recipe Costing</span>
            <span className="w-1 h-1 rounded-full bg-[#E8643E]" />
            <span>Pricing</span>
            <span className="w-1 h-1 rounded-full bg-[#E8643E]" />
            <span>Production</span>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-[#F4ECE0] p-6 sm:p-7"
          data-testid="studio-login-card">
          {phase === 'phone' ? (
            <>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FEF1E2] border border-[#F4B860]/40 mb-3"
                data-testid="studio-beta-banner">
                <Sparkles className="w-3 h-3 text-[#D4A017]" />
                <span className="text-[10px] uppercase tracking-wider text-[#7A5A10] font-semibold">Public Beta Access</span>
              </div>
              <h2 className="text-base font-bold text-[#3A1F12] mb-4">Get started in 30 seconds</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Mobile number</label>
                  <Input value={phone} onChange={e => setPhone(e.target.value)}
                    inputMode="tel" placeholder="+91 9876543210"
                    data-testid="studio-phone-input"
                    className="h-11 mt-1 text-sm bg-white/80" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#A0846B] font-semibold">Display name (optional)</label>
                  <Input value={name} onChange={e => setName(e.target.value)}
                    placeholder="Chef Anika"
                    data-testid="studio-name-input"
                    className="h-11 mt-1 text-sm bg-white/80" />
                </div>
                <Button onClick={requestOtp} disabled={submitting || !phone.trim()}
                  data-testid="studio-request-otp-btn"
                  className="w-full h-11 mt-2 bg-gradient-to-r from-[#E8643E] to-[#D44E2A] hover:from-[#D44E2A] hover:to-[#B73C1B] text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all">
                  {submitting ? 'Sending OTP…' : <>Send OTP <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
                {error && <p className="text-xs text-[#C45A41] text-center" data-testid="studio-login-error">{error}</p>}
              </div>
              <p className="text-[10px] text-[#A0846B] text-center mt-4">
                No password. No setup. Your workspace is ready the moment you verify.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-base font-bold text-[#3A1F12] mb-1">Enter the 6-digit OTP</h2>
              <p className="text-xs text-[#7A2A12] mb-4">
                Sent to <span className="font-semibold">{phone}</span>
              </p>
              <div className="space-y-3">
                <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric" placeholder="••••••" autoFocus
                  data-testid="studio-otp-input"
                  className="h-12 text-center text-xl tracking-[0.4em] font-bold tabular-nums bg-white/80" />
                {betaMode && betaOtp && (
                  <div className="rounded-lg px-3 py-2.5 flex items-start gap-2 bg-gradient-to-r from-[#FEF1E2] to-[#FCE7D6] border border-[#F4B860]/50"
                    data-testid="studio-beta-otp-banner">
                    <Sparkles className="w-3.5 h-3.5 text-[#D4A017] mt-0.5 shrink-0" />
                    <div className="leading-tight">
                      <p className="text-[10px] uppercase tracking-wider text-[#7A5A10] font-bold">Public Beta Access</p>
                      <p className="text-[12px] text-[#3A1F12] font-medium mt-0.5">
                        Use OTP <span className="font-bold tabular-nums tracking-wide">{betaOtp}</span> to enter Studio.
                      </p>
                      <p className="text-[10px] text-[#7A5A4B] mt-0.5">Real SMS delivery activates after public-beta validation.</p>
                    </div>
                  </div>
                )}
                {!betaMode && devOtp && (
                  <div className="bg-[#FEF6E0] border border-[#F4B860]/50 rounded-lg px-3 py-2 flex items-center gap-2"
                    data-testid="studio-dev-otp-banner">
                    <Sparkles className="w-3 h-3 text-[#D4A017]" />
                    <span className="text-[11px] text-[#7A5A10]">
                      Dev mode OTP: <span className="font-bold tabular-nums">{devOtp}</span>
                    </span>
                  </div>
                )}
                <Button onClick={verify} disabled={submitting || otp.length !== 6}
                  data-testid="studio-verify-otp-btn"
                  className="w-full h-11 bg-gradient-to-r from-[#E8643E] to-[#D44E2A] hover:from-[#D44E2A] hover:to-[#B73C1B] text-white font-semibold rounded-xl shadow-md">
                  {submitting ? 'Verifying…' : 'Verify & Enter Studio'}
                </Button>
                <div className="flex items-center justify-between text-[11px]">
                  <button type="button" onClick={() => { setPhase('phone'); setOtp(''); setDevOtp(''); setBetaOtp(''); setBetaMode(false); setError(''); }}
                    data-testid="studio-change-phone-btn"
                    className="text-[#7A2A12] hover:text-[#3A1F12] flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" /> Change number
                  </button>
                  <button type="button" onClick={requestOtp} disabled={submitting}
                    data-testid="studio-resend-otp-btn"
                    className="text-[#7A2A12] hover:text-[#3A1F12]">Resend OTP</button>
                </div>
                {error && <p className="text-xs text-[#C45A41] text-center" data-testid="studio-login-error">{error}</p>}
              </div>
            </>
          )}
        </div>

        <button type="button" onClick={() => navigate('/login')}
          data-testid="studio-enterprise-link"
          className="block text-[11px] text-[#A0846B] hover:text-[#3A1F12] mt-6 mx-auto">
          Are you an enterprise admin? Sign in here →
        </button>
      </div>
    </div>
  );
}
